import { useState, useEffect, useContext } from 'react'
import I18n from 'i18n-js'
import 'date-fns'
import { useHistory, useParams, Link } from 'react-router-dom'
import {
  Box,
  Grid,
  Typography,
  Button,
  CircularProgress,
  Card,
  TextField,
  Fade,
} from '@mui/material'
import LocalizationProvider from '@mui/lab/LocalizationProvider'
import AdapterDateFns from '@mui/lab/AdapterDateFns'
import { MobileDatePicker } from '@mui/lab'
import { CheckCircle } from '@mui/icons-material'

import { AuthContext } from '../../../context/auth'
import { canAccess } from '../../../router/permissions'
import { actionKeys, dateLocales, pagesKeys } from '../../../utils'
import CoordinatingStaffValidator from '../../../validators/coordinatingStaffValidator'
import {
  InputForm,
  SelectForm,
  CrudsBtn,
  DinamicForm,
  SnackBar,
} from '../../../components'

// services
import {
  createCoordinatingStaff,
  showCoordinatingStaff,
  updateCoordinatingStaff,
} from '../../../services/coordinatingStaff'
import medicalCenters from '../../../services/medicalsCenter'
import users from '../../../services/users'
import useStyles from './style'
import { isEmail } from '../../../validators/validator'
import { transfromLocalePhone } from '../../../utils/locale'
import PhoneSplit from '../../../components/phoneSplit'

const pad = (n) => String(n).padStart(2, '0')

// Backend expects YYYY-MM-DD
const formatBirth = (value) => {
  if (!value) return null
  if (value instanceof Date) {
    if (isNaN(value.getTime())) return null
    return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(
      value.getDate()
    )}`
  }
  return String(value).slice(0, 10)
}

// API may return 'Féminin' / 'F' / 0 for female
// sex is optional for coordinators: an unset value must stay empty, not read as female
const sexToId = (sex) => {
  if (sex === null || sex === undefined || sex === '') return ''
  return !sex || sex === 'Féminin' || sex === 'F' ? 0 : 1
}

const Form = () => {
  const classes = useStyles()
  const params = useParams()
  const history = useHistory()
  const [oldEmail, setOldEmail] = useState()
  //permissions
  const pageId = pagesKeys.coordinatingStaff

  //state
  const { state } = useContext(AuthContext)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const [medicalCenterList, setMedicalCenterList] = useState([])
  // Form
  const [errors, setErrors] = useState({})
  const [disabled, setDisabled] = useState(true)
  const [disableEmail, setdisableEmail] = useState(false)
  const [currentField, setCurrentField] = useState('')
  const [form, setForm] = useState({
    medical_center_ids: [],
  })
  const [finished, setFinished] = useState(false)
  const [show, setShow] = useState(false)
  const [message, setMessage] = useState({ text: '', variant: 'warning' })

  // Methods
  const handleForm = async (e) => {
    const { name, value } = e.target

    setCurrentField(name)
    setForm({ ...form, [name]: name === 'email' ? value.toLowerCase() : value })

    if (name === 'email') {
      if (!isEmail(value) && value !== oldEmail) {
        const json = await users.checkEmail(value)
        if (json.data) {
          setErrors((prev) => ({
            ...prev,
            email: I18n.t('validators.emailExist'),
          }))
          setdisableEmail(true)
        } else {
          setdisableEmail(false)
        }
      } else {
        setdisableEmail(false)
      }
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (disabled || disableEmail) return
    setSaving(true)

    const body = {
      email: form.email,
      name: form.name,
      lastname: form.lastname || null,
      sex: form.sex === '' || form.sex === undefined ? null : form.sex,
      birth: formatBirth(form.birth),
      phone: form.phone || null,
      medical_center_ids: form.medical_center_ids,
    }

    const json = params.id
      ? await updateCoordinatingStaff(params.id, body)
      : await createCoordinatingStaff(body)

    if (json?.data) {
      setFinished(true)
    } else {
      const backendMessage =
        typeof json?.error?.message === 'string' ? json.error.message : null
      setMessage({
        text: backendMessage || I18n.t('coordinatingStaff.error'),
        variant: 'warning',
      })
      setShow(true)
    }
    setSaving(false)
  }

  // Fetch
  const getCoordinator = async () => {
    const res = await showCoordinatingStaff(params.id)
    const coordinator = res?.data?.coordinatingStaff
    if (!coordinator) {
      history.push('/dashboard/coordinating-staff')
      return null
    }
    setOldEmail(coordinator.email)
    return {
      id: coordinator.id,
      email: coordinator.email || '',
      name: coordinator.name || '',
      lastname: coordinator.lastname || '',
      sex: sexToId(coordinator.sex),
      phone: coordinator.phone || '',
      birth: coordinator.birth || null,
      medical_center_ids: (coordinator.medical_centers || []).map(
        (center) => center.id
      ),
    }
  }

  const getData = async () => {
    const centers = await medicalCenters.list()
    setMedicalCenterList(
      (centers?.data?.medicalCenters || []).map((item) => ({
        id: item.id,
        name: item.name,
      }))
    )

    if (params.id) {
      const coordinator = await getCoordinator()
      if (coordinator) setForm(coordinator)
    }
  }

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      await getData()
      setLoading(false)
    }
    load()
  }, [])

  useEffect(() => {
    const allowed = params.id
      ? canAccess(actionKeys.edit, pageId, state)
      : canAccess(actionKeys.create, pageId, state)
    if (allowed) {
      const validator = CoordinatingStaffValidator(form, currentField, errors)
      // nothing touched yet: a blank form must not open with errors showing
      setErrors({
        ...(currentField ? validator.errors : {}),
        // keep the "already exists" message set by the email check
        ...(disableEmail ? { email: I18n.t('validators.emailExist') } : {}),
      })
      setDisabled(!validator.valid)
    } else {
      history.push('/dashboard')
    }
  }, [form, disableEmail])

  const showError = (name) => Boolean(errors[name]) && form[name] !== undefined

  const textFields = [
    {
      name: 'name',
      label: `${I18n.t('coordinatingStaff.form.first_name')} (*)`,
      size: { xs: 6, lg: 4 },
    },
    {
      name: 'lastname',
      label: I18n.t('coordinatingStaff.form.last_name'),
      size: { xs: 6, lg: 4 },
    },
    {
      name: 'email',
      label: `${I18n.t('coordinatingStaff.form.mail_address')} (*)`,
      size: { xs: 6, lg: 4 },
    },
  ]

  return (
    <Box p={4}>
      <Grid container spacing={2} direction="column">
        <Grid item className={classes.items}>
          <Typography variant="body2" color="textSecondary">
            {I18n.t('breadcrumb.coordinatingStaff')} /{' '}
            <Link
              to="/dashboard/coordinating-staff"
              className={classes.textBreadcrum}
            >
              {I18n.t('breadcrumb.dashboard')}
            </Link>{' '}
            / {!params.id && I18n.t('breadcrumb.add')}
            {params.id && I18n.t('breadcrumb.edit')}
          </Typography>
        </Grid>

        {!finished ? (
          <>
            <Grid item className={classes.itemFlex}>
              <Typography
                variant="h5"
                color="primary"
                className={classes.title}
              >
                {!params.id && I18n.t('coordinatingStaff.create')}
                {params.id && I18n.t('coordinatingStaff.edit')}
              </Typography>
            </Grid>

            {loading ? (
              <CircularProgress className={classes.loading} />
            ) : (
              <Grid item className={classes.items}>
                <Card bgcolor="white">
                  <Box p={4}>
                    <form action="" onSubmit={handleSubmit}>
                      <Grid container spacing={3} direction="row">
                        <Grid item xs={12} className={classes.gridSubtitle}>
                          <Typography variant="h6" color="primary">
                            {I18n.t('coordinatingStaff.form.personalInformation')}
                          </Typography>
                        </Grid>

                        {textFields.map((item) => (
                          <Grid
                            item
                            xs={item.size.xs}
                            lg={item.size.lg}
                            style={{ padding: '17px 12px' }}
                            key={item.name}
                          >
                            <InputForm
                              name={item.name}
                              color="secondary"
                              onChange={handleForm}
                              value={form[item.name] ?? ''}
                              label={item.label}
                              error={showError(item.name)}
                              helperText={
                                showError(item.name) ? errors[item.name] : ''
                              }
                              fullWidth
                              variant="outlined"
                              style={{
                                marginBottom: showError(item.name)
                                  ? '-16px'
                                  : '',
                              }}
                            />
                          </Grid>
                        ))}

                        <Grid
                          item
                          xs={6}
                          lg={4}
                          style={{ padding: '17px 12px' }}
                        >
                          <PhoneSplit
                            color="secondary"
                            fullWidth
                            variant="outlined"
                            label={I18n.t('coordinatingStaff.form.phone_number')}
                            onChange={(event) => {
                              handleForm({
                                target: { name: 'phone', value: event },
                              })
                            }}
                            value={form.phone || ''}
                            defaultCountry={
                              transfromLocalePhone[state.locale] || 'fr'
                            }
                            helperText={showError('phone') ? errors.phone : ''}
                            error={showError('phone')}
                          />
                        </Grid>

                        <Grid
                          item
                          xs={6}
                          lg={4}
                          style={{ padding: '17px 12px' }}
                        >
                          <SelectForm
                            name="sex"
                            label={I18n.t('coordinatingStaff.form.gender')}
                            color="secondary"
                            variant="outlined"
                            onChange={handleForm}
                            value={
                              form.sex < 0 || form.sex == undefined
                                ? ''
                                : form.sex
                            }
                            fullWidth
                            options={[
                              {
                                id: 0,
                                name: I18n.t('patient.form.gender.female'),
                              },
                              {
                                id: 1,
                                name: I18n.t('patient.form.gender.male'),
                              },
                            ]}
                          />
                        </Grid>

                        <Grid
                          item
                          xs={6}
                          lg={4}
                          style={{ padding: '17px 12px' }}
                        >
                          <LocalizationProvider
                            dateAdapter={AdapterDateFns}
                            locale={dateLocales[state.locale]}
                          >
                            <MobileDatePicker
                              toolbarTitle=""
                              renderInput={(props) => (
                                <TextField
                                  {...props}
                                  fullWidth
                                  color="secondary"
                                  variant="outlined"
                                  label={I18n.t(
                                    'coordinatingStaff.form.date_of_birth'
                                  )}
                                  name="birth"
                                  className={classes.input}
                                />
                              )}
                              inputFormat={
                                I18n.t('general.dateFormat') || 'dd-MM-yyyy'
                              }
                              onChange={(value) =>
                                handleForm({
                                  target: { name: 'birth', value },
                                })
                              }
                              maxDate={new Date()}
                              value={form.birth ?? null}
                            />
                          </LocalizationProvider>
                        </Grid>

                        <Grid item xs={12} className={classes.gridSubtitle}>
                          <Typography variant="h6" color="primary">
                            {I18n.t('coordinatingStaff.form.centersInformation')}
                          </Typography>
                        </Grid>

                        <Grid
                          item
                          xs={12}
                          lg={8}
                          style={{ padding: '17px 12px' }}
                        >
                          <DinamicForm
                            inputs={[
                              {
                                name: 'medical_center_ids',
                                label: `${I18n.t(
                                  'coordinatingStaff.form.medical_centers'
                                )} (*)`,
                                type: 'multiple',
                                options: medicalCenterList,
                                fullWidth: true,
                              },
                            ]}
                            handleChange={handleForm}
                            values={form}
                          />
                          {errors.medical_center_ids && (
                            <Typography
                              variant="caption"
                              color="error"
                              component="p"
                            >
                              {errors.medical_center_ids}
                            </Typography>
                          )}
                        </Grid>

                        <Grid item xs={12} style={{ marginTop: '20px' }}>
                          <Grid container justifyContent="space-between">
                            <Button
                              variant="text"
                              color="secondary"
                              style={{ textTransform: 'none' }}
                              onClick={() => history.goBack()}
                            >
                              {I18n.t('components.cancel')}
                            </Button>
                            <CrudsBtn
                              type="submit"
                              disabled={disabled || disableEmail || saving}
                              text={I18n.t('coordinatingStaff.save')}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </form>
                  </Box>
                </Card>
              </Grid>
            )}
          </>
        ) : (
          <Fade in={finished} timeout={500}>
            <Box className={classes.finishContainer}>
              <CheckCircle className={classes.chekCircle} />
              <Typography variant="h5" color="primary">
                {I18n.t('components.success')}
              </Typography>
              <Typography variant="body1" color="textPrimary">
                {I18n.t(
                  params.id ? 'coordinatingStaff.edited' : 'coordinatingStaff.created'
                )}
              </Typography>
              <Box className={classes.footerEnd}>
                <CrudsBtn
                  text={I18n.t('coordinatingStaff.back')}
                  component={Link}
                  to="/dashboard/coordinating-staff"
                />
              </Box>
            </Box>
          </Fade>
        )}
      </Grid>
      <SnackBar
        text={message.text}
        severity={message.variant}
        show={show}
        HandleShow={setShow}
        time={3000}
      />
    </Box>
  )
}

export default Form
